import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Save, Mail, Star, Eye, EyeOff, Globe, Image as ImageIcon, Upload, ToggleLeft, ToggleRight, Settings2, CreditCard, Smartphone, MessageSquare, DollarSign } from 'lucide-react';
import api from '../../api/client';
import { useCurrency, COUNTRY_CURRENCY_MAP, COUNTRY_REGION_DEFAULTS } from '../../context/CurrencyContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function SettingsTab({ onModulesChanged }) {
  const { setLocalPreference, symbol } = useCurrency();
  // ... keep rest
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Snapshot of the last confirmed-saved state; used to compute dirty flag
  const savedRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showMpesaSecret, setShowMpesaSecret] = useState(false);
  const [showMpesaPasskey, setShowMpesaPasskey] = useState(false);
  const [showSmsApiKey, setShowSmsApiKey] = useState(false);
  const [showSmsPassword, setShowSmsPassword] = useState(false);
  const [showSmsAccessKey, setShowSmsAccessKey] = useState(false);
  const [showZeptoKey, setShowZeptoKey] = useState(false);
  const [showMailchimpKey, setShowMailchimpKey] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState('');
  const [activeSection, setActiveSection] = useState('points');

  useEffect(() => {
    api.get('/api/config/admin/settings/flat')
      .then(res => {
        setSettings(res.data);
        savedRef.current = res.data; // baseline — save button starts disabled
      })
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  // Compare all non-metadata fields as strings (handles "587" vs 587 coercion)
  function settingsEqual(a, b) {
    if (!a || !b) return true;
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    keys.delete('_settingsVersion'); // metadata, not a user field
    for (const k of keys) {
      if (String(a[k] ?? '') !== String(b[k] ?? '')) return false;
    }
    return true;
  }

  const isDirty = settings !== null && !settingsEqual(settings, savedRef.current);

  const handleSave = async (e) => {
    e.preventDefault();
    // Guard: prevent API call when nothing has actually changed
    if (!isDirty) {
      toast.info('No values changed');
      return;
    }
    setSaving(true);
    try {
      const res = await api.put('/api/config/admin/settings', settings);
      // Reset baseline → save button becomes disabled again immediately
      setSettings(res.data);
      savedRef.current = res.data;
      toast.success('Settings saved');
      if (onModulesChanged) onModulesChanged();
      if (res.data.currency_country && res.data.currency_selectedCurrency) {
        setLocalPreference(res.data.currency_country, res.data.currency_selectedCurrency, {
          timezone: res.data.currency_timezone,
          locale: res.data.currency_locale,
        });
      }
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(
          err.response?.data?.message ||
          'Settings were updated by another user. Please reload and re-apply your changes.',
          { duration: 7000 }
        );
      } else {
        toast.error(err.displayMessage || 'Failed to save settings');
      }
    } finally { setSaving(false); }
  };

  const handleImageUpload = async (file, field) => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/api/config/storage/upload/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSettings(s => ({ ...s, [field]: res.data.url }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Upload failed');
    }
  };

  const update = (key, value) => setSettings(s => ({ ...s, [key]: value }));

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)}</div>;
  if (!settings) return null;

  const sections = [
    { id: 'modules', label: 'Modules', icon: Settings2 },
    { id: 'points', label: 'Points Config', icon: Star },
    { id: 'platinum', label: 'Platinum', icon: CreditCard },
    { id: 'mpesa', label: 'M-Pesa', icon: Smartphone },
    { id: 'sms', label: 'Bulk SMS', icon: MessageSquare },
    { id: 'smtp', label: 'Email Server', icon: Mail },
    { id: 'site', label: 'Site Content', icon: Globe },
    { id: 'currency', label: 'Currency Config', icon: DollarSign },
  ];

  return (
    <div data-testid="settings-tab">
      {/* Section Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
        {sections.map(s => {
          const Icon = s.icon;
          return (
            <button key={s.id} onClick={() => setActiveSection(s.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${activeSection === s.id ? 'bg-[#561D1A] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-[#561D1A]/30'}`}>
              <Icon size={14} /> {s.label}
            </button>
          );
        })}
      </div>

      <form onSubmit={handleSave} className="max-w-2xl space-y-6">
        {/* Module Toggles */}
        {activeSection === 'modules' && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm" data-testid="modules-section">
            <h3 className="text-sm font-bold text-[#561D1A] uppercase tracking-wider mb-1">Module Management</h3>
            <p className="text-xs text-gray-400 mb-5">Enable or disable admin modules. Disabled modules will be hidden from the sidebar.</p>
            <div className="space-y-1">
              {[
                { key: 'module_stores', label: 'Stores', desc: 'Manage store locations and configurations' },
                { key: 'module_voucher_types', label: 'Voucher Types', desc: 'Create and manage voucher categories' },
                { key: 'module_vouchers', label: 'Vouchers', desc: 'View and manage issued vouchers' },
                { key: 'module_users', label: 'Users & Roles', desc: 'User accounts and role management' },
                { key: 'module_membership', label: 'Membership', desc: 'Membership tiers and enrollments' },
                { key: 'module_platinum', label: 'Platinum', desc: 'WExclusive Platinum membership programme' },
                { key: 'module_agreements', label: 'Agreements', desc: 'Merchant partnership agreements' },
                { key: 'module_settlements', label: 'Settlements', desc: 'Settlement batches and payouts' },
                { key: 'module_reports', label: 'Reports', desc: 'Business metrics and analytics' },
                { key: 'module_onboarding', label: 'Onboarding', desc: 'Store setup wizard' },
                { key: 'module_audit_log', label: 'Audit Log', desc: 'Transaction audit trail for investigation and analytics' },
              ].map(mod => {
                const enabled = settings[mod.key] !== false;
                return (
                  <div key={mod.key} className={`flex items-center justify-between p-3.5 rounded-xl transition-all ${enabled ? 'bg-white hover:bg-gray-50' : 'bg-gray-50/50'}`} data-testid={`module-toggle-${mod.key}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium text-sm ${enabled ? 'text-gray-800' : 'text-gray-400'}`}>{mod.label}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${enabled ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}>
                          {enabled ? 'ON' : 'OFF'}
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-400 mt-0.5">{mod.desc}</p>
                    </div>
                    <button type="button" onClick={async () => {
                        const newVal = !enabled;
                        update(mod.key, newVal);
                        try {
                          // Send current _settingsVersion so the backend can check for conflicts
                          const res = await api.put('/api/config/admin/settings', {
                            [mod.key]: newVal,
                            _settingsVersion: settings._settingsVersion,
                          });
                          // Sync savedRef so the main Save button remains disabled for unchanged fields
                          setSettings(res.data);
                          savedRef.current = res.data;
                          if (onModulesChanged) onModulesChanged();
                        } catch (err) {
                          if (err.response?.status === 409) {
                            toast.error('Conflict detected — reload the page and try again.');
                          } else {
                            toast.error('Failed to toggle module');
                          }
                          // Revert optimistic UI update on error
                          update(mod.key, enabled);
                        }
                      }}
                      className={`shrink-0 transition-colors ${enabled ? 'text-emerald-500' : 'text-gray-300'}`}
                      data-testid={`toggle-${mod.key}`}>
                      {enabled ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Points Redemption */}
        {activeSection === 'points' && (
          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 bg-[#D4AF37]/20 rounded-xl flex items-center justify-center"><Star size={18} className="text-[#D4AF37]" /></div>
              <div>
                <h3 className="font-bold text-[#561D1A]">Points Redemption</h3>
                <p className="text-xs text-gray-400">Configure loyalty points conversion rate</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Points per {symbol} 1</label>
                <input type="number" step="0.1" min="0.1" value={settings.points_to_kes_rate}
                  onChange={e => update('points_to_kes_rate', parseFloat(e.target.value) || 10)}
                  className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="points-rate-input" />
                <p className="text-xs text-gray-400 mt-1">{settings.points_to_kes_rate} points = {symbol} 1 discount</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Min Points to Redeem</label>
                <input type="number" min="1" value={settings.min_points_to_redeem}
                  onChange={e => update('min_points_to_redeem', parseInt(e.target.value) || 100)}
                  className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="min-points-input" />
              </div>
            </div>
          </div>
        )}

        {/* Platinum Configuration */}
        {activeSection === 'platinum' && (
          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 bg-[#D4AF37]/20 rounded-xl flex items-center justify-center"><CreditCard size={18} className="text-[#D4AF37]" /></div>
              <div>
                <h3 className="font-bold text-[#561D1A]">Platinum Card QR Settings</h3>
                <p className="text-xs text-gray-400">Configure the base URL for Platinum card QR code verification</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Verification Base URL</label>
                <input value={settings.platinum_verify_url || ''} onChange={e => update('platinum_verify_url', e.target.value)}
                  placeholder="https://yourdomain.com"
                  className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="platinum-verify-url-input" />
                <p className="text-xs text-gray-400 mt-1">
                  When set, the QR code on Platinum cards will encode a scannable URL. Merchants scanning the QR with their phone camera will be redirected to
                  <code className="text-[#561D1A] bg-gray-50 px-1 rounded mx-1 text-[11px]">{settings.platinum_verify_url || 'https://yourdomain.com'}/platinum/verify?qr=...</code>
                </p>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Site URL (Fallback)</label>
                <input value={settings.site_url || ''} onChange={e => update('site_url', e.target.value)}
                  placeholder="https://yourdomain.com"
                  className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="site-url-input" />
                <p className="text-xs text-gray-400 mt-1">Used as fallback if Verification Base URL is not set</p>
              </div>
              <div className="border-t border-gray-100 pt-4 mt-4">
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Admin Notification Email</label>
                <input type="email" value={settings.admin_notification_email || ''} onChange={e => update('admin_notification_email', e.target.value)}
                  placeholder="admin@yourcompany.com"
                  className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="admin-notification-email-input" />
                <p className="text-xs text-gray-400 mt-1">Receives comprehensive email reports for every Platinum discount transaction (member details, merchant, store, device info)</p>
              </div>
            </div>
          </div>
        )}

        {/* M-Pesa Daraja API */}
        {activeSection === 'mpesa' && (
          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-testid="mpesa-section">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 bg-green-50 rounded-xl flex items-center justify-center"><Smartphone size={18} className="text-green-600" /></div>
              <div>
                <h3 className="font-bold text-[#561D1A]">M-Pesa Daraja API</h3>
                <p className="text-xs text-gray-400">Configure Safaricom M-Pesa STK Push integration for voucher payments</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Environment</label>
                <select value={settings.mpesa_environment || 'sandbox'} onChange={e => update('mpesa_environment', e.target.value)}
                  className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="mpesa-environment-select">
                  <option value="sandbox">Sandbox (Testing)</option>
                  <option value="production">Production (Live)</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  {settings.mpesa_environment === 'production'
                    ? 'Live environment — real transactions will be processed'
                    : 'Sandbox mode — use Safaricom test credentials'}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Consumer Key</label>
                  <input value={settings.mpesa_consumer_key || ''} onChange={e => update('mpesa_consumer_key', e.target.value)}
                    placeholder="Your Daraja app consumer key"
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="mpesa-consumer-key-input" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Consumer Secret</label>
                  <div className="relative">
                    <input type={showMpesaSecret ? 'text' : 'password'} value={settings.mpesa_consumer_secret || ''} onChange={e => update('mpesa_consumer_secret', e.target.value)}
                      placeholder="Your Daraja app consumer secret"
                      className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent pr-8" data-testid="mpesa-consumer-secret-input" />
                    <button type="button" onClick={() => setShowMpesaSecret(!showMpesaSecret)} className="absolute right-0 top-2 text-gray-400 hover:text-gray-600">
                      {showMpesaSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Business Shortcode</label>
                  <input value={settings.mpesa_shortcode || ''} onChange={e => update('mpesa_shortcode', e.target.value)}
                    placeholder="174379"
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="mpesa-shortcode-input" />
                  <p className="text-xs text-gray-400 mt-1">Paybill or Till number registered on Daraja</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Lipa Na M-Pesa Passkey</label>
                  <div className="relative">
                    <input type={showMpesaPasskey ? 'text' : 'password'} value={settings.mpesa_passkey || ''} onChange={e => update('mpesa_passkey', e.target.value)}
                      placeholder="Passkey from Daraja portal"
                      className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent pr-8" data-testid="mpesa-passkey-input" />
                    <button type="button" onClick={() => setShowMpesaPasskey(!showMpesaPasskey)} className="absolute right-0 top-2 text-gray-400 hover:text-gray-600">
                      {showMpesaPasskey ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">STK Push Callback URL</label>
                <input value={settings.mpesa_callback_url || ''} onChange={e => update('mpesa_callback_url', e.target.value)}
                  placeholder="https://yourdomain.com/api/payments/mpesa/callback"
                  className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="mpesa-callback-url-input" />
                <p className="text-xs text-gray-400 mt-1">Publicly accessible URL where Safaricom sends payment confirmation</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Account Reference</label>
                  <input value={settings.mpesa_account_reference || ''} onChange={e => update('mpesa_account_reference', e.target.value)}
                    placeholder="LuxeMall"
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="mpesa-account-ref-input" />
                  <p className="text-xs text-gray-400 mt-1">Displayed on customer's M-Pesa statement</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Transaction Description</label>
                  <input value={settings.mpesa_transaction_desc || ''} onChange={e => update('mpesa_transaction_desc', e.target.value)}
                    placeholder="Gift Voucher Purchase"
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="mpesa-transaction-desc-input" />
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mt-2">
                <p className="text-xs text-amber-800">
                  <strong>Setup Guide:</strong> Register at{' '}
                  <a href="https://developer.safaricom.co.ke" target="_blank" rel="noopener noreferrer" className="underline font-medium">developer.safaricom.co.ke</a>,
                  create an app under Lipa Na M-Pesa Online, and copy the credentials above. Use sandbox credentials for testing before switching to production.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Bulk SMS (Onfon Media) */}
        {activeSection === 'sms' && (
          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-testid="sms-section">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 bg-teal-50 rounded-xl flex items-center justify-center"><MessageSquare size={18} className="text-teal-600" /></div>
              <div>
                <h3 className="font-bold text-[#561D1A]">Bulk SMS (Onfon Media)</h3>
                <p className="text-xs text-gray-400">Configure Onfon Media API for sending bulk SMS notifications</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">SMS API URL</label>
                  <input value={settings.sms_api_url || ''} onChange={e => update('sms_api_url', e.target.value)}
                    placeholder="https://api.onfonmedia.co.ke/v1/sms/SendBulkSMS"
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="sms-api-url-input" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Authorization URL</label>
                  <input value={settings.sms_auth_url || ''} onChange={e => update('sms_auth_url', e.target.value)}
                    placeholder="https://apis.onfonmedia.co.ke/v1/authorization"
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="sms-auth-url-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">API Key</label>
                  <div className="relative">
                    <input type={showSmsApiKey ? 'text' : 'password'} value={settings.sms_api_key || ''} onChange={e => update('sms_api_key', e.target.value)}
                      placeholder="Your Onfon Media API key"
                      className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent pr-8" data-testid="sms-api-key-input" />
                    <button type="button" onClick={() => setShowSmsApiKey(!showSmsApiKey)} className="absolute right-0 top-2 text-gray-400 hover:text-gray-600">
                      {showSmsApiKey ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Client ID</label>
                  <input value={settings.sms_client_id || ''} onChange={e => update('sms_client_id', e.target.value)}
                    placeholder="Your Onfon Media client ID"
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="sms-client-id-input" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Access Key</label>
                <div className="relative">
                  <input type={showSmsAccessKey ? 'text' : 'password'} value={settings.sms_access_key || ''} onChange={e => update('sms_access_key', e.target.value)}
                    placeholder="Access key for API headers"
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent pr-8" data-testid="sms-access-key-input" />
                  <button type="button" onClick={() => setShowSmsAccessKey(!showSmsAccessKey)} className="absolute right-0 top-2 text-gray-400 hover:text-gray-600">
                    {showSmsAccessKey ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Sender ID</label>
                <input value={settings.sms_sender_id || ''} onChange={e => update('sms_sender_id', e.target.value)}
                  placeholder="e.g. LuxeMall"
                  className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="sms-sender-id-input" />
                <p className="text-xs text-gray-400 mt-1">Alphanumeric name shown as the SMS sender on recipient's phone</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">API Username</label>
                  <input value={settings.sms_api_username || ''} onChange={e => update('sms_api_username', e.target.value)}
                    placeholder="Onfon API username"
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="sms-api-username-input" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">API Password</label>
                  <div className="relative">
                    <input type={showSmsPassword ? 'text' : 'password'} value={settings.sms_api_password || ''} onChange={e => update('sms_api_password', e.target.value)}
                      placeholder="Onfon API password"
                      className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent pr-8" data-testid="sms-api-password-input" />
                    <button type="button" onClick={() => setShowSmsPassword(!showSmsPassword)} className="absolute right-0 top-2 text-gray-400 hover:text-gray-600">
                      {showSmsPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 mt-2">
                <p className="text-xs text-teal-800">
                  <strong>How it works:</strong> The system authenticates via the Authorization URL using your username and password, then sends SMS through the Bulk SMS API endpoint using the Access Key header. The Sender ID appears as the sender name on recipient phones.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Email server providers */}
        {activeSection === 'smtp' && (() => {
          const activeProvider = settings.email_active_provider || 'smtp';
          const emailProviders = [
            {
              id: 'smtp',
              title: 'Generic SMTP',
              blurb: 'Any SMTP server (Office 365, Gmail, custom mail relay)',
            },
            {
              id: 'zeptomail',
              title: 'Zoho ZeptoMail',
              blurb: 'ZeptoMail SMTP or API token for transactional email',
            },
            {
              id: 'mailchimp',
              title: 'Mailchimp Transactional',
              blurb: 'Mandrill API key for transactional messages',
            },
          ];

          const handleTestEmail = async () => {
            setTestingEmail(true);
            try {
              // Persist current form first so the test uses latest values
              if (isDirty) {
                const res = await api.put('/api/config/admin/settings', settings);
                setSettings(res.data);
                savedRef.current = res.data;
              }
              const res = await api.post('/api/config/admin/settings/email/test', {
                provider: settings.email_active_provider || 'smtp',
                testEmail: testEmailTo || undefined,
              });
              toast.success(res.data?.message || 'Test email sent');
            } catch (err) {
              toast.error(err.displayMessage || err.response?.data?.message || 'Email test failed');
            } finally {
              setTestingEmail(false);
            }
          };

          return (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center"><Mail size={18} className="text-blue-500" /></div>
                <div>
                  <h3 className="font-bold text-[#561D1A]">Email Server</h3>
                  <p className="text-xs text-gray-400">Choose the provider used for purchase, gift, agreement, and system emails</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-2">
                {emailProviders.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => update('email_active_provider', p.id)}
                    className={`text-left rounded-xl border p-4 transition-all ${activeProvider === p.id ? 'border-[#561D1A] bg-[#561D1A]/5 ring-1 ring-[#561D1A]/30' : 'border-gray-200 hover:border-[#561D1A]/40'}`}
                    data-testid={`email-provider-${p.id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-sm text-[#561D1A]">{p.title}</span>
                      <span className={`w-3.5 h-3.5 rounded-full border ${activeProvider === p.id ? 'bg-[#561D1A] border-[#561D1A]' : 'border-gray-300'}`} />
                    </div>
                    <p className="text-xs text-gray-500 leading-snug">{p.blurb}</p>
                  </button>
                ))}
              </div>
            </div>

            {activeProvider === 'smtp' && (
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <h3 className="font-bold text-[#561D1A] mb-1">Generic SMTP</h3>
                <p className="text-xs text-gray-400 mb-5">Microsoft 365, Gmail app password, or any SMTP relay</p>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">SMTP Host</label>
                      <input value={settings.smtp_host || ''} onChange={e => update('smtp_host', e.target.value)} placeholder="smtp.office365.com"
                        className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="smtp-host-input" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">SMTP Port</label>
                      <input type="number" value={settings.smtp_port || 587} onChange={e => update('smtp_port', parseInt(e.target.value) || 587)}
                        className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="smtp-port-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Username / Email</label>
                      <input value={settings.smtp_username || ''} onChange={e => update('smtp_username', e.target.value)} placeholder="noreply@yourdomain.com"
                        className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="smtp-username-input" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Password</label>
                      <div className="relative">
                        <input type={showPassword ? 'text' : 'password'} value={settings.smtp_password || ''} onChange={e => update('smtp_password', e.target.value)} placeholder="App password"
                          className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent pr-8" data-testid="smtp-password-input" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-0 top-2 text-gray-400 hover:text-gray-600">
                          {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">From Email</label>
                      <input value={settings.smtp_from_email || ''} onChange={e => update('smtp_from_email', e.target.value)} placeholder="noreply@yourdomain.com"
                        className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="smtp-from-email-input" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">From Name</label>
                      <input value={settings.smtp_from_name || ''} onChange={e => update('smtp_from_name', e.target.value)}
                        className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="smtp-from-name-input" />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer pt-1">
                    <input type="checkbox" checked={settings.smtp_use_tls !== false && settings.smtp_use_tls !== 'false'} onChange={e => update('smtp_use_tls', e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-[#561D1A] focus:ring-[#561D1A]" />
                    <span className="text-sm text-gray-600">Use STARTTLS</span>
                  </label>
                </div>
              </div>
            )}

            {activeProvider === 'zeptomail' && (
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <h3 className="font-bold text-[#561D1A] mb-1">Zoho ZeptoMail</h3>
                <p className="text-xs text-gray-400 mb-5">Use SMTP (default) with your Send Mail Token, or switch to HTTP API</p>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Send Mode</label>
                    <select
                      value={settings.zeptomail_send_mode || 'smtp'}
                      onChange={e => update('zeptomail_send_mode', e.target.value)}
                      className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent"
                      data-testid="zeptomail-send-mode"
                    >
                      <option value="smtp">SMTP (smtp.zeptomail.com)</option>
                      <option value="api">HTTP API (api.zeptomail.com)</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">SMTP Host</label>
                      <input value={settings.zeptomail_host || 'smtp.zeptomail.com'} onChange={e => update('zeptomail_host', e.target.value)}
                        className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="zeptomail-host-input" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">SMTP Port</label>
                      <input type="number" value={settings.zeptomail_port || 587} onChange={e => update('zeptomail_port', parseInt(e.target.value) || 587)}
                        className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="zeptomail-port-input" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Send Mail Token / API Key</label>
                    <div className="relative">
                      <input type={showZeptoKey ? 'text' : 'password'} value={settings.zeptomail_api_key || ''} onChange={e => update('zeptomail_api_key', e.target.value)}
                        placeholder="Zoho ZeptoMail send mail token"
                        className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent pr-8" data-testid="zeptomail-api-key-input" />
                      <button type="button" onClick={() => setShowZeptoKey(!showZeptoKey)} className="absolute right-0 top-2 text-gray-400 hover:text-gray-600">
                        {showZeptoKey ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">For SMTP mode, username is typically <code>emailapikey</code> and the token is the password.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">SMTP Username</label>
                      <input value={settings.zeptomail_username || 'emailapikey'} onChange={e => update('zeptomail_username', e.target.value)}
                        className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="zeptomail-username-input" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">From Email</label>
                      <input value={settings.zeptomail_from_email || ''} onChange={e => update('zeptomail_from_email', e.target.value)} placeholder="noreply@yourdomain.com"
                        className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="zeptomail-from-email-input" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">From Name</label>
                    <input value={settings.zeptomail_from_name || ''} onChange={e => update('zeptomail_from_name', e.target.value)}
                      className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="zeptomail-from-name-input" />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer pt-1">
                    <input type="checkbox" checked={settings.zeptomail_use_tls !== false && settings.zeptomail_use_tls !== 'false'} onChange={e => update('zeptomail_use_tls', e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-[#561D1A] focus:ring-[#561D1A]" />
                    <span className="text-sm text-gray-600">Use STARTTLS (SMTP mode)</span>
                  </label>
                </div>
              </div>
            )}

            {activeProvider === 'mailchimp' && (
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <h3 className="font-bold text-[#561D1A] mb-1">Mailchimp Transactional</h3>
                <p className="text-xs text-gray-400 mb-5">Uses the Mandrill Transactional API (not Marketing campaigns)</p>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">API Key</label>
                    <div className="relative">
                      <input type={showMailchimpKey ? 'text' : 'password'} value={settings.mailchimp_api_key || ''} onChange={e => update('mailchimp_api_key', e.target.value)}
                        placeholder="Mailchimp Transactional / Mandrill API key"
                        className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent pr-8" data-testid="mailchimp-api-key-input" />
                      <button type="button" onClick={() => setShowMailchimpKey(!showMailchimpKey)} className="absolute right-0 top-2 text-gray-400 hover:text-gray-600">
                        {showMailchimpKey ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">From Email</label>
                      <input value={settings.mailchimp_from_email || ''} onChange={e => update('mailchimp_from_email', e.target.value)} placeholder="noreply@yourdomain.com"
                        className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="mailchimp-from-email-input" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">From Name</label>
                      <input value={settings.mailchimp_from_name || ''} onChange={e => update('mailchimp_from_name', e.target.value)}
                        className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="mailchimp-from-name-input" />
                    </div>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3">
                    <p className="text-xs text-yellow-900">
                      Create a Transactional (Mandrill) API key in Mailchimp → Transactional → Settings. Marketing-only keys will not send.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
              <h3 className="font-bold text-[#561D1A] mb-1">Send test email</h3>
              <p className="text-xs text-gray-400 mb-4">Saves pending changes, then sends a probe message using the selected provider</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={testEmailTo}
                  onChange={e => setTestEmailTo(e.target.value)}
                  placeholder="you@example.com (optional — defaults to From Email)"
                  className="flex-1 border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent"
                  data-testid="email-test-recipient-input"
                />
                <button
                  type="button"
                  onClick={handleTestEmail}
                  disabled={testingEmail}
                  className="px-4 py-2 rounded-xl bg-[#561D1A] text-white text-sm font-medium disabled:opacity-60"
                  data-testid="email-test-button"
                >
                  {testingEmail ? 'Sending…' : 'Send test'}
                </button>
              </div>
            </div>
          </div>
          );
        })()}

        {/* Site Customization */}
        {activeSection === 'site' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 bg-purple-50 rounded-xl flex items-center justify-center"><Globe size={18} className="text-purple-500" /></div>
                <div>
                  <h3 className="font-bold text-[#561D1A]">Hero Section</h3>
                  <p className="text-xs text-gray-400">Customize the shop page hero banner</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Badge Text</label>
                  <input value={settings.hero_badge_text || ''} onChange={e => update('hero_badge_text', e.target.value)}
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="hero-badge-input" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Headline</label>
                  <input value={settings.hero_title || ''} onChange={e => update('hero_title', e.target.value)}
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="hero-title-input" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Subtitle</label>
                  <textarea value={settings.hero_subtitle || ''} onChange={e => update('hero_subtitle', e.target.value)} rows={2}
                    className="w-full border-2 border-gray-200 focus:border-[#561D1A] p-2 text-sm outline-none rounded-xl bg-transparent resize-none" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-2">Hero Image</label>
                  {settings.hero_image_url && (
                    <img src={settings.hero_image_url.startsWith('http') ? settings.hero_image_url : `${API_BASE}${settings.hero_image_url}`}
                      alt="Hero" className="h-20 rounded-lg object-cover mb-2" />
                  )}
                  <input type="file" accept="image/*" onChange={e => handleImageUpload(e.target.files[0], 'hero_image_url')} className="text-xs" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 bg-orange-50 rounded-xl flex items-center justify-center"><ImageIcon size={18} className="text-orange-500" /></div>
                <div>
                  <h3 className="font-bold text-[#561D1A]">Promo Banner</h3>
                  <p className="text-xs text-gray-400">Promotional banner on the shop page</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Banner Title</label>
                  <input value={settings.promo_banner_title || ''} onChange={e => update('promo_banner_title', e.target.value)}
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Banner Subtitle</label>
                  <input value={settings.promo_banner_subtitle || ''} onChange={e => update('promo_banner_subtitle', e.target.value)}
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-2">Banner Image</label>
                  {settings.promo_banner_image_url && (
                    <img src={settings.promo_banner_image_url.startsWith('http') ? settings.promo_banner_image_url : `${API_BASE}${settings.promo_banner_image_url}`}
                      alt="Banner" className="h-20 rounded-lg object-cover mb-2" />
                  )}
                  <input type="file" accept="image/*" onChange={e => handleImageUpload(e.target.files[0], 'promo_banner_image_url')} className="text-xs" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
              <h3 className="font-bold text-[#561D1A] mb-4">Stats & Contact</h3>
              <div className="grid grid-cols-2 gap-4">
                {[
                  ['stats_stores', 'Partner Stores Count'],
                  ['stats_vouchers', 'Vouchers Issued Count'],
                  ['stats_rating', 'Customer Rating'],
                  ['stats_delivery', 'Delivery Speed'],
                  ['footer_email', 'Contact Email'],
                  ['footer_phone', 'Contact Phone'],
                ].map(([key, label]) => (
                  <div key={key}>
                    <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">{label}</label>
                    <input value={settings[key] || ''} onChange={e => update(key, e.target.value)}
                      className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Currency Config */}
        {activeSection === 'currency' && (() => {
          const selectedCountry = settings.currency_country || '';
          const availableCurrencies = COUNTRY_CURRENCY_MAP[selectedCountry] || [];
          const selectedCurrencyObj = availableCurrencies.find(
            c => c.code === settings.currency_selectedCurrency
          );
          const region = COUNTRY_REGION_DEFAULTS[selectedCountry] || COUNTRY_REGION_DEFAULTS.Kenya;

          const handleCountryChange = (country) => {
            const list = COUNTRY_CURRENCY_MAP[country] || [];
            const autoSelected = list.length === 1 ? list[0].code : '';
            const nextRegion = COUNTRY_REGION_DEFAULTS[country] || COUNTRY_REGION_DEFAULTS.Kenya;
            update('currency_country', country);
            update('currency_selectedCurrency', autoSelected);
            update('currency_timezone', nextRegion.timezone);
            update('currency_locale', nextRegion.locale);
          };

          return (
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-testid="currency-section">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 bg-emerald-50 rounded-xl flex items-center justify-center">
                  <DollarSign size={18} className="text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-bold text-[#561D1A]">Currency &amp; Region</h3>
                  <p className="text-xs text-gray-400">
                    Country, currency, timezone and locale applied across Gift Voucher sessions
                  </p>
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Country</label>
                  <select value={selectedCountry} onChange={e => handleCountryChange(e.target.value)}
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent"
                    data-testid="currency-country-select">
                    <option value="">— Select a country —</option>
                    {Object.keys(COUNTRY_CURRENCY_MAP).map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Currency</label>
                  <select
                    value={settings.currency_selectedCurrency || ''}
                    onChange={e => update('currency_selectedCurrency', e.target.value)}
                    disabled={availableCurrencies.length <= 1}
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="currency-currency-select">
                    <option value="">— Select a currency —</option>
                    {availableCurrencies.map(c => (
                      <option key={c.code} value={c.code}>{c.code} ({c.symbol})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Timezone</label>
                  <input
                    type="text"
                    value={settings.currency_timezone || region.timezone}
                    onChange={e => update('currency_timezone', e.target.value)}
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent"
                    placeholder="Africa/Nairobi"
                    data-testid="currency-timezone-input"
                  />
                  <p className="text-xs text-gray-400 mt-1">IANA timezone (e.g. Africa/Nairobi)</p>
                </div>

                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Locale</label>
                  <input
                    type="text"
                    value={settings.currency_locale || region.locale}
                    onChange={e => update('currency_locale', e.target.value)}
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent"
                    placeholder="en-KE"
                    data-testid="currency-locale-input"
                  />
                  <p className="text-xs text-gray-400 mt-1">BCP 47 locale for date and number formatting</p>
                </div>

                {settings.currency_selectedCurrency && selectedCurrencyObj && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                    <p className="text-xs text-emerald-800">
                      <strong>Active configuration:</strong>{' '}
                      {settings.currency_country} — {settings.currency_selectedCurrency} ({selectedCurrencyObj.symbol})
                      {' '}· {settings.currency_timezone || region.timezone}
                      {' '}· {settings.currency_locale || region.locale}
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        <button
          type="submit"
          disabled={!isDirty || saving}
          className={`w-full py-3 rounded-full text-sm font-medium flex items-center justify-center gap-2 transition-all
            ${isDirty && !saving
              ? 'bg-[#EC1A23] hover:bg-[#C4121A] text-white hover:shadow-lg cursor-pointer'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          data-testid="save-settings-btn"
        >
          {saving
            ? 'Saving...'
            : isDirty
              ? <><Save size={15} /> Save Settings</>
              : 'No changes'
          }
        </button>
      </form>
    </div>
  );
}
